import { Badge, Stack, Table, Text } from '@mantine/core';
import {
  DNS_STATUS_COLOR,
  DNS_STATUS_LABEL,
  dnsRecordValue,
  dnsTtlLabel,
  type DnsZoneAssignmentStatus,
} from '../../lib/api/domains/dnsZones';

/** Gemeinsame Anzeige-Bausteine der DNS-Zonen (intern, Kunden-Flyout). */

export interface RecordLike {
  type: string;
  name: string;
  content: string | null;
  ttl: number | null;
  proxied: boolean | null;
  priority: number | null;
  data: unknown;
}

export interface RecordChangeLike {
  type: string;
  name: string;
  fields: string[];
  before: RecordLike;
  after: RecordLike;
}

const dateTime = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short', timeStyle: 'short' });

export function formatDnsTime(iso: string | null | undefined): string {
  return iso ? dateTime.format(new Date(iso)) : '—';
}

export function DnsStatusBadge({ status }: { status: DnsZoneAssignmentStatus }) {
  return (
    <Badge size="sm" variant="light" color={DNS_STATUS_COLOR[status]}>
      {DNS_STATUS_LABEL[status]}
    </Badge>
  );
}

export function DnsRecordTable({ records, showProxy = true }: { records: RecordLike[]; showProxy?: boolean }) {
  if (records.length === 0) return <Text size="sm" c="dimmed">Keine DNS-Einträge.</Text>;
  return (
    <Table.ScrollContainer minWidth={560}>
      <Table striped highlightOnHover verticalSpacing={4} fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Typ</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Wert</Table.Th>
            <Table.Th>TTL</Table.Th>
            {showProxy && <Table.Th>Proxy</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {records.map((r, i) => (
            <Table.Tr key={`${r.type}-${r.name}-${i}`}>
              <Table.Td><Badge size="xs" variant="outline">{r.type}</Badge></Table.Td>
              <Table.Td style={{ wordBreak: 'break-all' }}>{r.name}</Table.Td>
              <Table.Td style={{ wordBreak: 'break-all', fontFamily: 'var(--mantine-font-family-monospace)' }}>{dnsRecordValue(r)}</Table.Td>
              <Table.Td>{dnsTtlLabel(r.ttl)}</Table.Td>
              {showProxy && <Table.Td>{r.proxied ? 'ja' : r.proxied === false ? 'nein' : '—'}</Table.Td>}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

const FIELD_LABEL: Record<string, string> = { content: 'Wert', ttl: 'TTL', proxied: 'Proxy', priority: 'Priorität', data: 'Daten', comment: 'Kommentar', tags: 'Tags', type: 'Typ', name: 'Name' };

/** Record-Änderungen: hinzugefügt, entfernt, geändert — mit dem vorherigen Wert direkt daneben. */
export function DnsRecordChanges({ added, removed, changed }: { added: RecordLike[]; removed: RecordLike[]; changed: RecordChangeLike[] }) {
  if (added.length + removed.length + changed.length === 0) return <Text size="sm" c="dimmed">Keine Änderung an DNS-Einträgen.</Text>;
  return (
    <Stack gap={4}>
      {changed.map((c, i) => (
        <Text key={`c${i}`} size="sm">
          <Badge size="xs" color="orange" variant="light" mr={6}>geändert</Badge>
          <b>{c.type}</b> {c.name}: <span style={{ textDecoration: 'line-through', color: 'var(--mantine-color-red-7)' }}>{dnsRecordValue(c.before)}</span>
          {' → '}
          <span style={{ color: 'var(--mantine-color-green-8)' }}>{dnsRecordValue(c.after)}</span>
          {c.fields.some((f) => f !== 'content' && f !== 'data') && (
            <Text span size="xs" c="dimmed">
              {' '}({c.fields.map((f) => `${FIELD_LABEL[f] ?? f}: vorher ${f === 'ttl' ? dnsTtlLabel(c.before.ttl) : JSON.stringify((c.before as unknown as Record<string, unknown>)[f] ?? null)}`).join(', ')})
            </Text>
          )}
        </Text>
      ))}
      {added.map((r, i) => (
        <Text key={`a${i}`} size="sm">
          <Badge size="xs" color="green" variant="light" mr={6}>neu</Badge>
          <b>{r.type}</b> {r.name}: {dnsRecordValue(r)}
        </Text>
      ))}
      {removed.map((r, i) => (
        <Text key={`r${i}`} size="sm">
          <Badge size="xs" color="red" variant="light" mr={6}>entfernt</Badge>
          <b>{r.type}</b> {r.name}: <span style={{ textDecoration: 'line-through' }}>{dnsRecordValue(r)}</span>
        </Text>
      ))}
    </Stack>
  );
}
